import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Calendar, Shield, TrendingUp, Users, Award, Info, DollarSign, Home, Building, Briefcase, CheckCircle, LogOut } from "lucide-react";

import PitchDeckModal from "@/components/PitchDeckModal";
import { ExecutiveSummaryPopup } from "@/components/ui/executive-summary-popup";
import { InvestorLetterPopup, INVESTOR_LETTERS_DATA } from "@/components/ui/investor-letter-popup";
import CampaignUnlockForm, { getCampaignSession, clearCampaignSession, CampaignSession } from "@/components/CampaignUnlockForm";
import pitchDeckCover from "@/assets/pitch-deck-cover.png";
import ispaiGiblet from "@/assets/ispai-giblet.png";
import ispaiLogoMark from "@/assets/ispai-logo-mark.png";
import a16zCryptoLogo from "@/assets/a16z-crypto-logo.png";
import mitLogo from "@/assets/mit-logo.png";
import cogentAiLogo from "@/assets/cogent-ai-logo.png";
import aigenseiLogo from "@/assets/aigensei-logo.png";
import andreesenHorowitzLogo from "@/assets/andreessen-horowitz-logo.png";
import openaiLogo from "@/assets/openai-logo.png";

const CAMPAIGN_CODE = "ispai2026";
const Invest = () => {
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRound, setSelectedRound] = useState(0);
  const [campaignSession, setCampaignSession] = useState<CampaignSession | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const session = getCampaignSession();
    if (session && (session.campaign_code === CAMPAIGN_CODE || session.campaign_code === 'registered' || session.is_admin)) {
      setCampaignSession(session);
      setIsUnlocked(true);
    }
  }, []);

  const handleUnlock = (session: CampaignSession) => {
    setCampaignSession(session);
    setIsUnlocked(true);
  };

  const handleLogout = () => {
    clearCampaignSession();
    setCampaignSession(null);
    setIsUnlocked(false);
    toast({
      title: "Logged Out",
      description: "You have been logged out of the investment terms."
    });
  };

  const [showPitchDeckModal, setShowPitchDeckModal] = useState(false);
  const [formData, setFormData] = useState({
    legalName: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    investorType: "",
    amount: ""
  });
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
    }
  };
  const investmentRounds = [{
    name: "Pre-Seed",
    amount: "$2M",
    description: "Early stage validation and team building",
    minimum: "$100K"
  }, {
    name: "Seed",
    amount: "$5M",
    description: "Product-market fit and initial scale",
    minimum: "$250K"
  }, {
    name: "Series A",
    amount: "$15M",
    description: "Scale operations and market expansion",
    minimum: "$500K"
  }];
  const documents = [{
    title: "Executive Summary",
    icon: "exec-summary-image",
    description: "High-level overview of iSPAI's vision and market opportunity"
  }, {
    title: "Pitch Deck",
    icon: "pitch-deck-image",
    description: "Comprehensive presentation of our business model and growth strategy"
  }, {
    title: "Investor Endorsements",
    icon: "investor-endorsements-image",
    description: "Read endorsement letters from our angel investors"
  }];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Insert into database
      const { error: dbError } = await supabase.from('investor_inquiries').insert({
        full_name: formData.legalName,
        legal_name: formData.legalName,
        email: formData.email,
        street_address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        country: formData.country,
        investor_type: formData.investorType,
        investment_amount: formData.amount,
        accredited_confirmed: true,
        campaign_source: 'angel_2026'
      });
      if (dbError) throw dbError;

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke('send-investor-notification', {
        body: {
          legal_name: formData.legalName,
          email: formData.email,
          investment_amount: formData.amount,
          investor_type: formData.investorType,
          street_address: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          country: formData.country,
          campaign_source: 'angel_2026',
          submitted_at: new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })
        }
      });

      if (emailError) {
        console.error("Email notification error:", emailError);
        // Don't fail the submission if email fails - data is saved
      }

      toast({
        title: "Request Submitted",
        description: "Thank you! You'll receive your term sheet within 24 hours."
      });
      setFormData({
        legalName: "",
        email: "",
        streetAddress: "",
        city: "",
        state: "",
        zipCode: "",
        country: "United States",
        investorType: "",
        amount: ""
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }}>
            <div className="flex justify-center mb-8">
              <img src="/lovable-uploads/a1b437ed-b1ee-4970-8136-8ffa5ecea125.png" alt="iSPAI Logo" className="h-32 w-auto" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Integrated Special Purpose AI
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">Join us in building the world's first knowledge network and marketplace for the wisdom of the workforce. Together we will define a new role for humans in an AI-driven future.</p>
            
        <div className="flex justify-center mt-12 mb-32">
          <Button onClick={() => scrollToSection("why-now")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
            Why Now?
          </Button>
        </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Why Now Section */}
        <motion.section id="why-now" className="mb-48" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }}>
          <h2 className="font-bold text-gray-900 mb-8 text-center text-4xl">Why Now?</h2>
          <div className="relative px-4">
            <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent" style={{ scrollbarWidth: 'thin' }}>
              {/* Card 1: a16z crypto (original) */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={a16zCryptoLogo} alt="a16z crypto" className="h-8 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "The web needs a new techno-economic model where value flows automatically. <span className="font-bold text-black">The key transition for the coming year will be moving from static licensing to real-time, usage-based compensation.</span> This means testing and scaling systems — potentially leveraging blockchain enabled nanopayments and sophisticated attribution standards — <span className="font-bold text-black">to automatically reward every entity that contributes information to an agent's successful task.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  ~Liz Harkavy, a16z crypto investment team (December 11, 2025)
                </p>
              </div>

              {/* Card 2: MIT */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={mitLogo} alt="MIT" className="h-24 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "<span className="font-bold text-black">Most AI deployments fail due to brittle workflows, lack of contextual learning, and misalignment with day-to-day operations.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  — MIT, State of AI in Business 2025
                </p>
              </div>

              {/* Card 3: a16z 2026 */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={andreesenHorowitzLogo} alt="Andreessen Horowitz" className="h-8 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "Data and analytics agents are essentially useless without the right context—they <span className="font-bold text-black">can't decipher business definitions or reason across systems effectively.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  — Andreessen Horowitz (a16z), 2026
                </p>
              </div>

              {/* Card 4: Jayesh Krishnan */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={cogentAiLogo} alt="Cogent AI" className="h-8 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "The breakthrough isn't better models—it's context. <span className="font-bold text-black">Systems that persist decision traces and reasoning will define the next generation of AI.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  — Jayesh Krishnan, Founder @ Cogent AI, 2026
                </p>
              </div>

              {/* Card 5: Context Engineering */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={aigenseiLogo} alt="aigensei" className="h-8 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "Enterprise AI fails when it can't answer a single question: 'How did you make that decision?' <span className="font-bold text-black">Context Engineering is what makes AI systems defensible.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  — Context Engineering: The Missing Discipline in Enterprise AI, 2026
                </p>
              </div>

              {/* Card 6: OpenAI */}
              <div className="min-w-[320px] md:min-w-[500px] max-w-3xl flex-shrink-0 snap-center bg-white rounded-lg shadow-lg p-8 md:p-12">
                <img src={openaiLogo} alt="OpenAI" className="h-8 mb-6" />
                <p className="text-gray-600 text-lg leading-relaxed">
                  "High-quality AI depends on rich, accurate context. <span className="font-bold text-black">Without it, even strong models produce incorrect results.</span>"
                </p>
                <p className="text-gray-500 mt-6 text-sm">
                  — OpenAI Engineering Blog, 2026
                </p>
              </div>
            </div>
            <p className="text-center text-gray-400 text-sm mt-2">← Scroll for more →</p>
          </div>
          
          <div className="flex justify-center mt-8 mb-64">
            <Button onClick={() => scrollToSection("why-ispai")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
              Why iSPAI?
            </Button>
          </div>
        </motion.section>

        {/* Value Proposition */}
        <motion.section id="why-ispai" className="mb-48" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }}>
          <h2 className="font-bold text-gray-900 mb-8 text-center text-4xl">Why iSPAI?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
              <CardHeader>
                <Users className="w-12 h-12 text-blue-600 mb-4" />
                <CardTitle>Massive Market Opportunity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">A multi-trillion dollar opportunity as AI adoption shifts from models to infrastructure where structured, validated context becomes essential for reliable, auditable, and scalable systems.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
              <CardHeader>
                <Shield className="w-12 h-12 text-green-600 mb-4" />
                <CardTitle>Proprietary Technology</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">iSPAI has developed a proprietary system for capturing, validating, and structuring human expertise into reusable context graphs that power reliable, auditable AI systems.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
              <CardHeader>
                <TrendingUp className="w-12 h-12 text-purple-600 mb-4" />
                <CardTitle>Traction-Building Momentum</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Strong pipeline of enterprises and agencies with a well-defined path forward to iterate product-market fit with users.</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex justify-center mt-8 mb-64">
            <Button onClick={() => scrollToSection("company-details")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
              About iSPAI
            </Button>
          </div>
        </motion.section>



        {/* Documents Section */}
        <motion.section id="company-details" className="mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.4
      }}>
          <h2 className="font-bold text-center mb-12 text-black text-4xl">About iSPAI</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {documents.map((doc, index) => {
            const content = <motion.div key={index} className="group cursor-pointer" onClick={() => {
              if (doc.title === "Pitch Deck") {
                setShowPitchDeckModal(true);
              }
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }}>
                  <Card className="border-2 border-gray-200 hover:border-gray-300 transition-all h-full">
                    <CardHeader>
                      <div className="w-16 h-16 flex items-center justify-center mb-4">
                        {doc.icon === "pitch-deck-image" ?
                    <img src={pitchDeckCover} alt="Pitch Deck" className="w-16 h-16 object-contain" /> :
                    doc.icon === "exec-summary-image" ?
                    <img src={ispaiGiblet} alt="Executive Summary" className="w-16 h-16 object-contain" /> :
                    doc.icon === "investor-endorsements-image" ?
                    <img src={ispaiLogoMark} alt="Investor Endorsements" className="w-16 h-16 object-contain" /> :

                    doc.icon
                    }
                      </div>
                      <CardTitle>{doc.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{doc.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>;
            if (doc.title === "Executive Summary") {
              return <ExecutiveSummaryPopup key={index} triggerElement={content} />;
            }
            if (doc.title === "Investor Endorsements") {
              return <InvestorLetterPopup key={index} letters={INVESTOR_LETTERS_DATA} triggerElement={content} />;
            }
            return content;
          })}
          </div>
        </motion.section>

        <div className="flex justify-center mt-8 mb-64">
          <Button onClick={() => scrollToSection("current-terms")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
            Terms Offered
          </Button>
        </div>
        {/* SAFE Terms - Prominent Display */}
        <motion.section id="current-terms" className="mb-32" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.0
      }}>
          {!isUnlocked ?
        <CampaignUnlockForm campaignCode={CAMPAIGN_CODE} onUnlock={handleUnlock} /> :

        <>
              {/* Connected container for both boxes */}
              <div className="border-2 border-black rounded-xl overflow-hidden">
                {/* Header with logout */}
                <div className="bg-white flex justify-center py-6 relative">
                  
                  <button
                onClick={handleLogout}
                className="absolute top-4 right-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">

                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>

                <div id="request-terms" className="bg-white p-8 pt-0">
                  <h2 className="font-bold text-center mb-2 text-black text-4xl">Current Investment Terms Offered</h2>
                  <div className="max-w-2xl mx-auto mb-6">
                    <p className="text-center text-lg font-semibold text-gray-600">We are raising $750,000 in this round via SAFE notes through Carta at a $12 million pre-money valuation cap.</p>
                  </div>
                  
                  <div className="text-center mb-8">
                    <div className="text-3xl font-bold text-black mb-2">$12M</div>
                    <div className="text-sm text-black">Pre-Money Valuation Cap</div>
                  </div>

                  <div className="max-w-md mx-auto mb-6">
                    <p className="text-sm text-gray-600 text-center">
                      Minimum investment allocation: $25,000. No discount applied.
                    </p>
                  </div>
                </div>

                {/* Bottom Section - Secure Your Investment Form */}
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-t-2 border-black p-8">
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-6 text-black">Investor Information</h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Legal Name *</label>
                        <Input type="text" required value={formData.legalName} onChange={(e) => handleInputChange('legalName', e.target.value)} className="w-full" placeholder="As it will appear on legal documents" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Investor Type *</label>
                        <Select value={formData.investorType} onValueChange={(value) => handleInputChange('investorType', value)} required>
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="Select investor type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="Individual">Individual</SelectItem>
                            <SelectItem value="Joint Tenants">Joint Tenants</SelectItem>
                            <SelectItem value="Trust">Trust</SelectItem>
                            <SelectItem value="Entity (LLC/Corp)">Entity (LLC/Corp)</SelectItem>
                            <SelectItem value="IRA/Retirement Account">IRA/Retirement Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
                        <Input type="text" required value={formData.streetAddress} onChange={(e) => handleInputChange('streetAddress', e.target.value)} className="w-full" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                          <Input type="text" required value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)} className="w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                          <Input type="text" required value={formData.state} onChange={(e) => handleInputChange('state', e.target.value)} className="w-full" placeholder="e.g., TX" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                          <Input type="text" required value={formData.zipCode} onChange={(e) => handleInputChange('zipCode', e.target.value)} className="w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                          <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)} required>
                            <SelectTrigger className="w-full bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="United States">United States</SelectItem>
                              <SelectItem value="Canada">Canada</SelectItem>
                              <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Investment Amount *</label>
                        <Select value={formData.amount} onValueChange={(value) => handleInputChange('amount', value)} required>
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="Select investment amount" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="$25,000">$25,000</SelectItem>
                            <SelectItem value="$50,000">$50,000</SelectItem>
                            <SelectItem value="$75,000">$75,000</SelectItem>
                            <SelectItem value="$100,000+">$100,000+</SelectItem>
                          </SelectContent>
                        </Select>
                        
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="accredited" required className="rounded" />
                        <label htmlFor="accredited" className="text-sm text-gray-700">
                          I confirm that I am an accredited investor as defined by SEC Rule 501
                        </label>
                      </div>
                      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3">
                        {isSubmitting ? "Submitting..." : "Request Carta Term Sheet"}
                      </Button>
                    </form>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-6 text-black">Next Steps</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                        <div>
                          <h4 className="font-medium text-gray-900">Term Sheet Review</h4>
                          <p className="text-gray-600 text-sm">Receive detailed SAFE agreement via Carta within 24 hours</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                        <div>
                          <h4 className="font-medium text-gray-900">Founder Discussion </h4>
                          <p className="text-gray-600 text-sm">Schedule investor call and access data room</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                        <div>
                          <h4 className="font-medium text-gray-900">Investment Execution</h4>
                          <p className="text-gray-600 text-sm">Sign documents and transfer funds through Carta</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-8 p-4 bg-white rounded-lg border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">⚡ Early Investor Benefits</h4>
                      <p className="text-gray-700 text-sm mb-2">Investors in this round receive:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Priority access to iSPAI's Seed round</li>
                        <li>• Exclusive access to the iSPAI data room in Carta</li>
                        <li>• Beta platform access</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Accredited Investor Verification */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-8">
                  <div className="flex items-start gap-3">
                    <div className="text-yellow-600 text-xl">⚠️</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-yellow-800">Accredited Investor Requirement</h4>
                        <Dialog>
                          <DialogTrigger asChild>
                            <button className="text-blue-600 hover:text-blue-800 transition-colors">
                              <Info className="w-4 h-4" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
                            <DialogHeader>
                              <DialogTitle className="text-xl font-bold text-center mb-4">
                                Who Qualifies as an Accredited Investor under SEC Rule 501?
                              </DialogTitle>
                            </DialogHeader>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Individual Investor Criteria */}
                              <div className="bg-gray-50 rounded-lg p-6">
                                <div className="flex items-center gap-2 mb-4">
                                  <Users className="w-6 h-6 text-blue-600" />
                                  <h3 className="text-lg font-semibold">Individual Investor Criteria</h3>
                                </div>
                                
                                <div className="space-y-4">
                                  <div className="flex items-start gap-3">
                                    <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Income Test</h4>
                                      <p className="text-sm text-gray-600">Earned $200,000/year ($300,000 with spouse) for the last 2 years</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <Home className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Net Worth Test</h4>
                                      <p className="text-sm text-gray-600">Net worth over $1 million, excluding primary residence</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Professional Credentials</h4>
                                      <p className="text-sm text-gray-600">Hold Series 7, 65, or 82 licenses</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Entity Investor Criteria */}
                              <div className="bg-gray-50 rounded-lg p-6">
                                <div className="flex items-center gap-2 mb-4">
                                  <Building className="w-6 h-6 text-blue-600" />
                                  <h3 className="text-lg font-semibold">Entity Investor Criteria</h3>
                                </div>
                                
                                <div className="space-y-4">
                                  <div className="flex items-start gap-3">
                                    <Briefcase className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Financial Institutions / Funds</h4>
                                      <p className="text-sm text-gray-600">Banks, broker-dealers, insurance companies, registered investment funds</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Private Entities</h4>
                                      <p className="text-sm text-gray-600">Assets over $5 million, not formed for the specific purpose of this investment</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-start gap-3">
                                    <Users className="w-5 h-5 text-green-600 mt-1" />
                                    <div>
                                      <h4 className="font-medium">Knowledgeable Employees</h4>
                                      <p className="text-sm text-gray-600">Certain employees of private funds qualify individually</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Compliance Reminder */}
                            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Shield className="w-5 h-5 text-blue-600" />
                                <h3 className="font-medium text-blue-800">Compliance Reminder</h3>
                              </div>
                              <p className="text-blue-700 text-sm">
                                Accredited Investor status must be verified for participation in 506(c) offerings.
                              </p>
                            </div>
                            
                            {/* Disclaimer */}
                            <div className="mt-4 text-xs text-gray-500 text-center border-t pt-4">
                              This is a simplified summary. Refer to SEC Rule 501 of Regulation D for the complete definition.
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                      <p className="text-yellow-700 text-sm">This offering is limited to accredited investors as defined by SEC regulations.</p>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </>
        }
        </motion.section>
      </div>


      {/* Pitch Deck Modal */}
      <PitchDeckModal open={showPitchDeckModal} onOpenChange={setShowPitchDeckModal} />
    </div>;
};
export default Invest;